/**
 * Shared labels and roll-ups for the traffic source drill-in.
 *
 * Channels are the classifier's buckets (paid / search / …). Origins are the exact thing inside
 * a bucket: a host, a utm_source token, a campaign name. The pie used to stop at the bucket.
 */

export const SOURCE_COLOURS = {
  paid: "orange.6",
  search: "blue.6",
  llm: "grape.6",
  social: "pink.6",
  referral: "teal.6",
  direct: "gray.6",
  unknown: "gray.4",
  organic: "blue.6", // demo-seed leftover; classifier writes "search"
};

export const SOURCE_LABELS = {
  paid: "Paid",
  search: "Search",
  llm: "LLMs",
  social: "Social",
  referral: "Referral",
  direct: "Direct",
  unknown: "Unknown",
  organic: "Search",
};

export const CHANNELS = ["paid", "search", "llm", "social", "referral", "direct", "unknown", "organic"];

/** Origin list heading inside a channel drawer — "referrers" is the wrong word for paid or search. */
export const ORIGIN_HEADINGS = {
  paid: "Networks",
  search: "Search engines",
  organic: "Search engines",
  llm: "Assistants",
  social: "Platforms",
  referral: "Referring sites",
  direct: "Origins",
  unknown: "Origins",
};

const KNOWN_ORIGINS = {
  "google.com": "Google",
  "google.de": "Google",
  "google.co.uk": "Google",
  "google.fr": "Google",
  google: "Google",
  "bing.com": "Bing",
  bing: "Bing",
  "duckduckgo.com": "DuckDuckGo",
  "yahoo.com": "Yahoo",
  "chatgpt.com": "ChatGPT",
  "chat.openai.com": "ChatGPT",
  "openai.com": "ChatGPT",
  "perplexity.ai": "Perplexity",
  "claude.ai": "Claude",
  "gemini.google.com": "Gemini",
  "reddit.com": "Reddit",
  "facebook.com": "Facebook",
  "l.facebook.com": "Facebook",
  "instagram.com": "Instagram",
  "linkedin.com": "LinkedIn",
  "x.com": "X",
  "twitter.com": "X",
  "t.co": "X",
  "tiktok.com": "TikTok",
  "youtube.com": "YouTube",
  "pinterest.com": "Pinterest",
};

export function sourceLabel(source) {
  return SOURCE_LABELS[source] ?? source;
}

export function sourceColour(source) {
  return SOURCE_COLOURS[source] ?? "gray.5";
}

/** Mantine token "blue.6" → CSS variable the ranked list can paint a dot with. */
export function sourceCssColour(source) {
  const token = sourceColour(source);
  const [name, shade = "6"] = token.split(".");
  return `var(--mantine-color-${name}-${shade})`;
}

export function originTitle(origin) {
  if (!origin) return "Unknown";
  const key = String(origin).replace(/^www\./i, "").toLowerCase();
  if (KNOWN_ORIGINS[key]) return KNOWN_ORIGINS[key];
  if (key.includes("google.")) return "Google";
  if (key.includes("bing.")) return "Bing";
  return origin;
}

export function flagEmoji(iso) {
  if (!iso || iso.length !== 2) return "";
  const code = iso.toUpperCase();
  return String.fromCodePoint(...[...code].map((c) => 127397 + c.charCodeAt(0)));
}

export function forChannel(rows, channel) {
  if (!channel) return rows ?? [];
  return (rows ?? []).filter((r) => r.source === channel);
}

/** Collapse source-tagged country rows into one row per country, busiest first. */
export function rollupCountries(rows) {
  const map = new Map();
  for (const row of rows ?? []) {
    const key = row.code || row.name;
    if (!key) continue;
    const prev = map.get(key);
    if (prev) {
      prev.sessions += row.sessions ?? 0;
    } else {
      map.set(key, {
        code: row.code,
        name: row.name || row.code,
        sessions: row.sessions ?? 0,
      });
    }
  }
  return [...map.values()].sort((a, b) => b.sessions - a.sessions);
}

export function rollupCities(rows, countryCode) {
  const map = new Map();
  for (const row of rows ?? []) {
    if (countryCode && row.countryCode !== countryCode) continue;
    const key = `${row.countryCode}|${row.city}`;
    if (!row.city) continue;
    const prev = map.get(key);
    if (prev) {
      prev.sessions += row.sessions ?? 0;
    } else {
      map.set(key, {
        city: row.city,
        countryCode: row.countryCode,
        countryName: row.countryName,
        sessions: row.sessions ?? 0,
      });
    }
  }
  return [...map.values()].sort((a, b) => b.sessions - a.sessions);
}

export function rollupLabels(rows, labelKey) {
  const map = new Map();
  for (const row of rows ?? []) {
    const label = row[labelKey] ?? row.origin ?? row.label;
    if (!label) continue;
    map.set(label, (map.get(label) ?? 0) + (row.sessions ?? 0));
  }
  return [...map.entries()]
    .map(([label, sessions]) => ({ label, sessions }))
    .sort((a, b) => b.sessions - a.sessions);
}
